import { useState, useMemo, useRef, useEffect } from "react";
import {
  Plus, ArrowDownUp, Search, Download, Eye, ListFilter, Check,
} from "lucide-react";
import {
  PageHeader, Button, DataTable, Pagination,
} from "../../shared/ui";
import StatusBadge from "./history/StatusBadge.jsx";
import JobDetailsModal from "./history/JobDetailsModal.jsx";
import { useHistoryData } from "../hooks/useHistoryData.js";
import { formatDateTime } from "../utils/dateUtils.js";
import { downloadFileFromUrl, isExternalDocumentUrl } from "../utils/downloadUtil.js";
import { HISTORY_STATUS_GROUPS, HISTORY_PAGE_SIZE_OPTIONS } from "../utils/constants.js";

const HistoryTab = ({ onNewGeneration }) => {
  const {
    records, total, totalPages, loading,
    search, setSearch,
    sortOrder, setSortOrder,
    status, setStatus,
    page, setPage,
    pageSize, setPageSize,
  } = useHistoryData();

  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setShowFilterMenu(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleFilterChange = (value) => {
    setStatus(value);
    setShowFilterMenu(false);
  };

  const handleDownload = async (record) => {
    if (!record.published_url || downloadingId) return;
    if (isExternalDocumentUrl(record.published_url)) {
      window.open(record.published_url, "_blank", "noopener,noreferrer");
      return;
    }
    setDownloadingId(record.job_id);
    try {
      await downloadFileFromUrl(record.published_url);
    } catch {
      // silently ignore
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "sr_no",
        header: "Sr. No.",
        width: 70,
        align: "center",
        sortAllowed: false,
        cell: ({ row }) => (
          <span className="font-semibold text-ink">{row.index + 1}</span>
        ),
      },
      {
        accessorKey: "document_title",
        header: "Document Title",
        sortAllowed: false,
        cell: ({ getValue }) => (
          <span className="font-medium text-ink truncate block max-w-48" title={getValue()}>
            {getValue() || "—"}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        width: 120,
        sortAllowed: false,
        cell: ({ getValue }) => <StatusBadge status={getValue()} />,
      },
      {
        accessorKey: "created_at",
        header: "Generated At",
        width: 180,
        sortAllowed: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted">
            {formatDateTime(row.original.completed_at || row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        width: 100,
        align: "right",
        sortAllowed: false,
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedJob(record)}
                aria-label="View details"
              >
                <Eye size={14} />
              </Button>
              {record.status === "completed" && record.published_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(record)}
                  disabled={!!downloadingId}
                  loading={downloadingId === record.job_id}
                  aria-label="Download document"
                >
                  {downloadingId === record.job_id ? null : <Download size={14} />}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [downloadingId]
  );

  const toolbarContent = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        title={sortOrder === "desc" ? "Newest first — click for oldest first" : "Oldest first — click for newest first"}
        onClick={() => setSortOrder((s) => (s === "desc" ? "asc" : "desc"))}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 transition-colors text-xs whitespace-nowrap outline-none focus:outline-none focus-visible:outline-none"
      >
        <ArrowDownUp size={13} />
        {sortOrder === "desc" ? "Newest first" : "Oldest first"}
      </button>

      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title or release tag…"
          className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-300 w-56"
        />
      </div>

      <div className="relative" ref={filterRef}>
        <button
          type="button"
          onClick={() => setShowFilterMenu((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 transition-colors text-sm outline-none focus:outline-none focus-visible:outline-none"
        >
          <ListFilter size={15} />
          {status !== "all" && (
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
          )}
        </button>
        {showFilterMenu && (
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 min-w-48">
            <button
              type="button"
              onClick={() => handleFilterChange("all")}
              className={[
                "w-full flex items-center justify-between px-3 py-1 text-xs transition-colors border-0 outline-none focus:outline-none focus-visible:outline-none",
                status === "all"
                  ? "bg-blue-50 text-blue-600 font-medium"
                  : "bg-white text-slate-500 hover:bg-gray-200",
              ].join(" ")}
            >
              <span>All statuses</span>
              {status === "all" && <Check size={11} className="text-blue-500 shrink-0" />}
            </button>

            {HISTORY_STATUS_GROUPS.map((group, groupIdx) => (
              <div key={group.label || groupIdx}>
                {group.label && (
                  <>
                    <div className="border-t border-gray-100 mt-1 mb-0.5" />
                    <p className="px-3 pt-1 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                      {group.label}
                    </p>
                  </>
                )}
                {group.options.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleFilterChange(value)}
                    className={[
                      "w-full flex items-center justify-between gap-2 px-3 py-0.5 transition-colors border-0 outline-none focus:outline-none focus-visible:outline-none",
                      value === status ? "bg-blue-50" : "bg-white hover:bg-gray-200",
                    ].join(" ")}
                  >
                    <StatusBadge status={value} />
                    {value === status && <Check size={11} className="text-blue-500 shrink-0" />}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const entriesStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const entriesEnd = Math.min(page * pageSize, total);
  const hasActiveFilters = Boolean(search) || status !== "all";
  const emptyMessage = hasActiveFilters
    ? "No results match your search or filters."
    : "No document generations yet. Click New Document Generation to get started.";

  return (
    <>
      <PageHeader
        title="Document Generation History"
        subtitle="All document generations, newest first."
        actions={
          <Button variant="primary" onClick={onNewGeneration}>
            <Plus size={15} /> New Document Generation
          </Button>
        }
      />

      <DataTable
        table={{ columns, rows: records }}
        canSearch={false}
        entriesPerPage={false}
        isSorted={false}
        showTotalEntries={false}
        autoResetPageIndex={false}
        toolbarContent={toolbarContent}
        emptyMessage={emptyMessage}
        loading={loading}
      />
      {!loading && records.length > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-3">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <select
              className="gl-input py-1 w-20 text-sm"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {HISTORY_PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>entries per page</span>
          </div>
          <p className="text-sm text-slate-500">
            Showing {entriesStart} to {entriesEnd} of {total} entries
          </p>
          {totalPages > 1 && (
            <div className="sm:ml-auto">
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </div>
      )}

      <JobDetailsModal
        job={selectedJob}
        onClose={() => setSelectedJob(null)}
        downloadingId={downloadingId}
        onDownload={handleDownload}
      />
    </>
  );
};

export default HistoryTab;
